import { FC, MouseEvent } from 'react';
import {
  AddRounded,
  CheckRounded,
  KeyboardArrowDownRounded,
  SearchRounded,
} from '@mui/icons-material';
import { Popover, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './TeamTopBar.scss';
import logoSrc from '../../../assets/logo/logo.png';
import { TeamListItem } from '../../../data-access';
import { Path } from '../../../routing';
import { getTeamInitials } from '../constants';
import TeamRoleChip from '../TeamRoleChip';

interface TeamTopBarProps {
  teamInitials: string;
  resolvedTeamName: string;
  activeTeamRole: string;
  activeTeamId?: string;
  isTeamDropdownOpen: boolean;
  teamDropdownAnchorEl: HTMLElement | null;
  teamSearch: string;
  filteredTeams: TeamListItem[];
  onToggleTeamDropdown: (event: MouseEvent<HTMLButtonElement>) => void;
  onCloseTeamDropdown: () => void;
  onTeamSearchChange: (value: string) => void;
  onSelectTeam: (teamId: string) => void;
  onOpenCreateTeam: () => void;
}

const TeamTopBar: FC<TeamTopBarProps> = ({
  teamInitials,
  resolvedTeamName,
  activeTeamRole,
  activeTeamId,
  isTeamDropdownOpen,
  teamDropdownAnchorEl,
  teamSearch,
  filteredTeams,
  onToggleTeamDropdown,
  onCloseTeamDropdown,
  onTeamSearchChange,
  onSelectTeam,
  onOpenCreateTeam,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleOpenTeamsHome = () => {
    onCloseTeamDropdown();
    navigate(Path.TEAM);
  };

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar__brand-button team-page__brand"
        onClick={handleOpenTeamsHome}
      >
        <img src={logoSrc} alt="" className="team-page__brand-logo" />
        <Typography variant="body2" component="span" className="team-page__brand-name">
          Lifestack
        </Typography>
      </button>
      <Typography variant="subtitle1" component="span" className="team-page__topbar-slash">
        /
      </Typography>

      <div className="team-page__team-switcher">
        <button
          type="button"
          className={`team-page__team-switcher-button${
            isTeamDropdownOpen ? ' team-page__team-switcher-button--open' : ''
          }`}
          aria-haspopup="menu"
          aria-expanded={isTeamDropdownOpen}
          onClick={onToggleTeamDropdown}
        >
          <span className="team-page__team-avatar team-page__team-avatar--topbar">
            {teamInitials}
          </span>
          <Typography variant="body2" component="span" className="team-page__team-switcher-name">
            {resolvedTeamName}
          </Typography>
          <KeyboardArrowDownRounded className="team-page__team-switcher-chevron" />
        </button>

        <Popover
          open={isTeamDropdownOpen}
          anchorEl={teamDropdownAnchorEl}
          onClose={onCloseTeamDropdown}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          className="topbar__team-dropdown-popover"
        >
          <div className="topbar__team-dropdown" role="menu">
            <div className="topbar__team-dropdown-search">
              <SearchRounded className="topbar__team-dropdown-search-icon" />
              <input
                value={teamSearch}
                onChange={(event) => onTeamSearchChange(event.target.value)}
                placeholder={t('team.topBar.findTeamPlaceholder')}
                className="topbar__team-dropdown-search-input"
                aria-label={t('team.topBar.findTeamAria')}
              />
            </div>

            <div className="topbar__team-dropdown-scroll">
              <Typography
                variant="subtitle2"
                component="div"
                className="topbar__team-dropdown-section-label"
              >
                {t('team.topBar.yourTeams')}
              </Typography>
              {filteredTeams.map(({ team }) => {
                const isActive = team.teamId === activeTeamId;

                return (
                  <button
                    key={team.teamId}
                    type="button"
                    role="menuitem"
                    className={`topbar__team-dropdown-team${
                      isActive ? ' topbar__team-dropdown-team--active' : ''
                    }`}
                    onClick={() => onSelectTeam(team.teamId)}
                  >
                    <span className="team-page__team-avatar team-page__team-avatar--dropdown">
                      {getTeamInitials(team.name)}
                    </span>
                    <span className="topbar__team-dropdown-team-copy">
                      <Typography
                        variant="subtitle1"
                        component="span"
                        className="topbar__team-dropdown-team-title"
                      >
                        {team.name}
                      </Typography>
                    </span>
                    {isActive && <CheckRounded className="topbar__team-dropdown-check" />}
                  </button>
                );
              })}

              {!filteredTeams.length && (
                <Typography variant="body2" component="div" className="topbar__team-dropdown-empty">
                  {t('team.topBar.noTeamsMatch')}
                </Typography>
              )}
            </div>

            <div className="topbar__team-dropdown-footer">
              <button
                type="button"
                className="topbar__team-dropdown-action"
                onClick={onOpenCreateTeam}
              >
                <AddRounded />
                <Typography variant="body2" component="span">
                  {t('team.topBar.createNewTeam')}
                </Typography>
              </button>
            </div>
          </div>
        </Popover>
      </div>

      <TeamRoleChip role={activeTeamRole} />
      <div className="team-page__topbar-spacer" />
    </header>
  );
};

export default TeamTopBar;
